import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import ButtonPrimary from '../button/ButtonPrimary';
import ButtonSecondary from '../button/ButtonSecondary';
import CustomPagination from './components/Pagination';
import CustomTable from './components/TableTemplate';
import PdfTemplate from './components/PdfTemplate';
import { pdf } from '@react-pdf/renderer';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useSetting } from '../../context/SettingContext';

const TableTemplate = ({ rows, columns, actions, filters, onAddNew, fileName = 'data' }) => {
    const [order, setOrder] = useState('asc');
    const [orderBy, setOrderBy] = useState(columns[0]?.field || '');
    const [selectedFilter, setSelectedFilter] = useState('default');
    const [filterValue, setFilterValue] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [searchQuery, setSearchQuery] = useState('');
    const isRowsEmpty = rows.length === 0;
    const { t } = useTranslation();
    const { setting } = useSetting();
    
    const handleSort = (field) => {
        if (orderBy === field) {
            setOrder(order === 'asc' ? 'desc' : 'asc');
        } else {
            setOrder('asc');
            setOrderBy(field);
        };
    };
    
    const handleFilterChange = (event) => {
        setSelectedFilter(event.target.value);
        setFilterValue('');
    };
    
    const handleFilterValueChange = (event) => {
        setFilterValue(event.target.value);
    };
    
    const filteredRows = useMemo(() => {
        let filteredData = rows;
        
        if (selectedFilter && selectedFilter !== 'default' && filterValue) {
            filteredData = rows.filter((row) => {
                const rowValue = row[selectedFilter];
                const rowValueLower = rowValue ? rowValue.toString().toLowerCase() : '';
                const filter = filters.find(f => f.field === selectedFilter && f.by.toLowerCase() === filterValue.toLowerCase());
                
                if (filter) {
                    if (filter.mode === 'exact') {
                        return rowValueLower === filterValue.toLowerCase();
                    } else if (filter.mode === 'contains') {
                        return rowValueLower.includes(filterValue.toLowerCase());
                    } else if (filter.mode === 'between') {
                        const [min, max] = filterValue.split('-').map(Number);
                        const numericValue = Number(rowValue);
                        
                        return min <= numericValue && numericValue <= max;
                    };
                };
                
                return true;
            });
        };
        
        return filteredData.filter((row) =>
            Object.values(row).join(' ').toLowerCase().includes(searchQuery.toLowerCase())
        );
    }, [selectedFilter, filterValue, searchQuery, rows, filters]);
    
    const sortedRows = useMemo(() => {
        return [...filteredRows].sort((a, b) => {
            if (orderBy) {
                const aValue = a[orderBy];
                const bValue = b[orderBy];
                
                const isDate = (value) => {
                    return !isNaN(new Date(value).getTime());
                };
                
                const parseDate = (dateString) => {
                    const [datePart, timePart] = dateString.split(' ');
                    const [day, month, year] = datePart.split('-').map(Number);
                    const [hour, minute, second] = timePart.split(':').map(Number);
                    const date = new Date(year, month - 1, day, hour, minute, second);
                    
                    return date;
                };
                
                const isDateField = orderBy.includes('_at') || orderBy.includes('registration_date');
                if (isDateField) {
                    const aDate = parseDate(aValue);
                    const bDate = parseDate(bValue);
                    
                    return order === 'asc' ? aDate - bDate : bDate - aDate;
                };
                
                if (typeof aValue === 'string' && typeof bValue === 'string') {
                    if (isDate(aValue) && isDate(bValue)) {
                        return order === 'asc' ? new Date(aValue) - new Date(bValue) : new Date(bValue) - new Date(aValue);
                    };
                    
                    return order === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
                };
                
                if (typeof aValue === 'number' && typeof bValue === 'number') {
                    return order === 'asc' ? aValue - bValue : bValue - aValue;
                };
                
                if (aValue instanceof Date && bValue instanceof Date) {
                    return order === 'asc' ? aValue - bValue : bValue - aValue;
                };
                
                return 0;
            };
            
            return 0;
        });
    }, [filteredRows, order, orderBy]);
    
    const visibleRows = useMemo(() => {
        const startIndex = (currentPage - 1) * rowsPerPage;
        const endIndex = startIndex + rowsPerPage;
        return sortedRows.slice(startIndex, Math.min(endIndex, filteredRows.length));
    }, [sortedRows, currentPage, rowsPerPage, filteredRows.length]);
    
    const handlePageChange = (page) => {
        setCurrentPage(page);
    };
    
    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(+event.target.value);
        setCurrentPage(1);
    };
    
    const totalPages = Math.ceil(filteredRows.length / rowsPerPage);
    
    const downloadCSV = () => {
        // Get current date and time in Indonesia (WIB - UTC +7)
        const now = new Date();
        
        // Adjust to Indonesia time zone (Asia/Jakarta)
        const indonesiaTime = new Date(now.toLocaleString('en-US', { timeZone: 'Asia/Jakarta' }));
        
        // Format date: DD/MM/YYYY
        const day = String(indonesiaTime.getDate()).padStart(2, '0');
        const month = String(indonesiaTime.getMonth() + 1).padStart(2, '0');
        const year = indonesiaTime.getFullYear();
        
        // Format time: HH:mm:ss
        const hours = String(indonesiaTime.getHours()).padStart(2, '0');
        const minutes = String(indonesiaTime.getMinutes()).padStart(2, '0');
        const seconds = String(indonesiaTime.getSeconds()).padStart(2, '0');
        
        // Final formatted string for the timestamp (DDMMYYYYHHMMSS)
        const timestamp = `${day}${month}${year}${hours}${minutes}${seconds}`;
        
        // Format fileName to lowercase and replace spaces with underscores
        const formattedFileName = fileName.toLowerCase().replace(/\s+/g, '_') + `_${timestamp}.csv`;
        
        const dataToDownload = filteredRows.length > 0 ? filteredRows : rows;
        if (dataToDownload.length === 0) {
            toast.error(t('table.noDataAvailableDownload'));
            return;
        };
        
        // Create CSV content
        const header = columns.map(col => col.title).join(',') + '\n';
        const body = dataToDownload.map(row => columns.map(col => row[col.field] || '').join(',')).join('\n');
        const csvContent = header + body;
        
        // Create a Blob and trigger download
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = formattedFileName;
        link.click();
    };
    
    const downloadPDF = () => {
        const data = filteredRows.length > 0 ? filteredRows : rows;
        if (data.length === 0) {
            toast.error(t('table.noDataAvailableDownload'));
            return;
        };
        
        const doc = (
            <PdfTemplate
                columns={columns}
                rows={data}
                logoUrl={setting.logo_url}
                title={setting[`title_${t('languages.select')}`]}
            />
        );
        
        pdf(doc).toBlob().then((blob) => {
            const link = document.createElement('a');
            link.href = URL.createObjectURL(blob);
            link.download = fileName;
            link.click();
        });
    };
    
    return (
        <div className='flex flex-col rounded-lg bg-white space-y-5'>
            {/* Search and Add New Buttons */}
            <div className='flex px-5 pt-5 items-center justify-between space-x-5'>
                <div className='flex flex-grow items-center space-x-2'>
                    <span className='text-xs md:text-sm'>{t('table.search')}:</span>
                    <input
                        className={`
                            w-full px-3 py-2 rounded border border-platinum
                            hover:outline-none hover:border-grayX11
                            focus:outline-none focus:border-blackOlive text-xs md:text-sm
                        `}
                        type='text'
                        placeholder={t('table.searchPlaceholder')}
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                    />
                </div>
                {onAddNew && (<ButtonPrimary className='h-full w-24 md:w-32 md:text-sm' onClick={onAddNew}>{t('table.add')}</ButtonPrimary>)}
            </div>
            
            {/* Sort Dropdown, Filter Column, & Download */}
            <div className='flex flex-col xl:flex-row px-5 items-center justify-center xl:justify-between space-y-2.5 xl:space-y-0 text-xs md:text-sm'>
                <div className='flex flex-col xl:flex-row items-center xl:space-x-5 space-y-2 xl:space-y-0'>
                    {/* Sort Dropdown */}
                    <div className='xl:flex items-center space-x-2'>
                        <span>{t('table.show')}</span>
                        <select
                            className={`
                                px-2 py-1 rounded bg-white border border-platinum text-black font-medium
                                hover:outline-none hover:border-grayX11
                                focus:outline-none focus:border-blackOlive
                            `}
                            value={rowsPerPage}
                            onChange={handleChangeRowsPerPage}
                        >
                            <option value={10}>10</option>
                            <option value={25}>25</option>
                            <option value={50}>50</option>
                            <option value={100}>100</option>
                        </select>
                        <span>{t('table.entries')}</span>
                    </div>
                    
                    {/* Filter Column */}
                    <div className='flex flex-col sm:flex-row sm:space-x-2.5 space-y-2.5 sm:space-y-0'>
                        <div className='xl:flex-col space-x-2'>
                            <span>{t('table.filter')}</span>
                            <select
                                className={`
                                    px-2 py-1 rounded bg-white border border-platinum text-black font-medium
                                    hover:outline-none hover:border-grayX11
                                    focus:outline-none focus:border-blackOlive
                                `}
                                value={selectedFilter}
                                onChange={handleFilterChange}
                                disabled={filters.length === 0}
                            >
                                <option value='default'>-</option>
                                {
                                    [...new Map(filters.map(filter => [filter.field, filter])).values()].map((filter) => (
                                        <option key={`${filter.field}-${filter.by}`} value={filter.field}>{filter.label}</option>
                                    ))
                                }
                            </select>
                        </div>
                        <div className='xl:flex-col space-x-[22.75px] sm:space-x-2'>
                            <span>{t('table.filterBy')}</span>
                            <select
                                className={`
                                    px-2 py-1 rounded bg-white border border-platinum text-black font-medium
                                    hover:outline-none hover:border-grayX11
                                    focus:outline-none focus:border-blackOlive
                                `}
                                onChange={handleFilterValueChange}
                                value={filterValue}
                                disabled={filters.length === 0 || selectedFilter === 'default'}
                            >
                                <option value='default' disabled={filterValue !== ''}>-</option>
                                {selectedFilter && selectedFilter !== 'default' && 
                                    filters.filter((f) => f.field === selectedFilter).map((filter) => (
                                        <option key={filter.by} value={filter.by}>{filter.labelBy}</option>
                                    ))
                                }
                            </select>
                        </div>
                    </div>
                </div>
                
                {/* Download CSV & PDF */}
                <div className='xl:flex space-x-2.5 md:space-x-5'>
                    <ButtonSecondary className='h-full w-24 md:w-32 md:text-sm' isDisabled={isRowsEmpty} onClick={downloadCSV}>CSV</ButtonSecondary>
                    <ButtonSecondary className='h-full w-24 md:w-32 md:text-sm' isDisabled={isRowsEmpty} onClick={downloadPDF}>PDF</ButtonSecondary>
                </div>
            </div>
            
            {/* Table */}
            <div className='overflow-x-auto'>
                <CustomTable
                    columns={columns}
                    rows={visibleRows}
                    actions={actions}
                    onSort={handleSort}
                    sortField={orderBy}
                    sortOrder={order}
                    currentPage={currentPage}
                    rowsPerPage={rowsPerPage}
                    t={t}
                />
            </div>
            
            {/* Pagination */}
            <div className='flex flex-col sm:flex-row px-5 pb-5 items-center justify-center sm:justify-between space-y-2.5 sm:space-y-0 text-xs md:text-sm overflow-x-auto'>
                <span className='text-grayX11'>
                    {t('table.showing')} {(filteredRows.length === 0 ? 0 : (currentPage - 1) * rowsPerPage + 1)} {t('table.to')}{' '}
                    {filteredRows.length === 0 ? 0 : Math.min(currentPage * rowsPerPage, filteredRows.length)}{' '} {t('table.of')} {filteredRows.length} {t('table.entries')}
                </span>
                <CustomPagination
                    currentPage={filteredRows.length === 0 ? 1 : currentPage}
                    totalPages={filteredRows.length === 0 ? 1 : totalPages}
                    onPageChange={handlePageChange}
                    t={t}
                />
            </div>
        </div>
    );
};

TableTemplate.propTypes = {
    rows: PropTypes.array.isRequired,
    columns: PropTypes.array.isRequired,
    actions: PropTypes.array,
    onAddNew: PropTypes.func,
    fileName: PropTypes.string,
};

export default TableTemplate;
